import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { ProtectedRoute } from './components/common';
import { HelmetProvider } from 'react-helmet-async';
import { Login, Register, VerifyAccount, ForgotPassword, ResetPassword } from './components/auth';
import ChangePassword from './components/auth/ChangePassword';
import { Home, Dashboard, CreateEvent, EventList, EditEvent, EventDetail, StudentEvents, StudentReadingHistory } from './pages';
import StudentEventDetail from './pages/StudentEventDetail';
import StudentParticipationHistory from './pages/StudentParticipationHistory';
import StudentPersonalCalendar from './pages/StudentPersonalCalendar';
import StudentProfile from './pages/StudentProfile';
import StudentSeries from './pages/StudentSeries';
import StudentSeriesDetail from './pages/StudentSeriesDetail';
import StudentMinigame from './pages/StudentMinigame';
import StudentMinigamePlay from './pages/StudentMinigamePlay';
import StudentMinigameHistory from './pages/StudentMinigameHistory';
import StudentPreparation from './pages/StudentPreparation';
import StudentPreparationDetail from './pages/StudentPreparationDetail';
import QRCodeCheckIn from './pages/QRCodeCheckIn';
import ArticleDetail from './pages/ArticleDetail';
import ArticleListPage from './pages/ArticleListPage';
import FeaturedArticlesPage from './pages/FeaturedArticlesPage';
import ArticlesByCategoryPage from './pages/ArticlesByCategoryPage';
import ArticlesByTagPage from './pages/ArticlesByTagPage';
import SearchArticlesPage from './pages/SearchArticlesPage';
import ArticleEditor from './pages/admin/ArticleEditor';
import ArticleEditorById from './pages/admin/ArticleEditorById';
import ArticleAnalyticsDashboard from './pages/admin/ArticleAnalyticsDashboard';
import AdminArticleManagement from './pages/admin/AdminArticleManagement';
import CategoriesManagement from './pages/admin/CategoriesManagement';
import TagsManagement from './pages/admin/TagsManagement';
import CommentManagement from './pages/admin/CommentManagement';
import StudentWishlist from './pages/StudentWishlist';
import SeriesManagement from './pages/admin/SeriesManagement';
import CreateSeries from './pages/admin/CreateSeries';
import EditSeries from './pages/admin/EditSeries';
import SeriesDetail from './pages/admin/SeriesDetail';
import MinigameManagement from './pages/admin/MinigameManagement';
import CreateMinigame from './pages/admin/CreateMinigame';
import CreateMinigameWizard from './pages/admin/CreateMinigameWizard';
import EditQuiz from './pages/admin/EditQuiz';
import AcademicStructure from './pages/admin/AcademicStructure';
import SemesterManagement from './pages/admin/SemesterManagement';
import Statistics from './pages/admin/Statistics';
import AccountManagement from './pages/admin/AccountManagement';
import EventRegistrations from './pages/admin/EventRegistrations';
import TaskManagement from './pages/admin/TaskManagement';
import ActivityTaskDashboard from './pages/admin/ActivityTaskDashboard';
import PreparationManagement from './pages/admin/PreparationManagement';
import PreparationDetail from './pages/admin/PreparationDetail';
import StudentRegistrations from './pages/StudentRegistrations';
import ManagerRegistrations from './pages/ManagerRegistrationsSplit';
import StudentTasks from './pages/StudentTasks';
import { Role } from './types';
import TaskSubmissionsRoute from './pages/TaskSubmissionsRoute';
// Removed criteria-based TrainingScore page
import ViewScores from './pages/ViewScores';
import ManagerScores from './pages/ManagerScores';
import StudentScoreAppeals from './pages/StudentScoreAppeals';
import StudentScoreAppealDetail from './pages/StudentScoreAppealDetail';
import ManagerScoreAppeals from './pages/ManagerScoreAppeals';
import SendEmail from './pages/admin/SendEmail';
import SendNotification from './pages/admin/SendNotification';
import EmailHistory from './pages/admin/EmailHistory';
import EmailDetail from './pages/admin/EmailDetail';
import NotificationList from './pages/NotificationList';
import NotificationDetail from './pages/NotificationDetail';
import AdminTools from './pages/admin/AdminTools';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ManagerLayout from './components/layout/ManagerLayout';

const queryClient = new QueryClient();

function App() {
  return (
    <Router>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <WishlistProvider>
              <div className="App">
                <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/articles" element={<ArticleListPage />} />
              <Route path="/articles/featured" element={<FeaturedArticlesPage />} />
              <Route path="/articles/search" element={<SearchArticlesPage />} />
              <Route path="/articles/category/:categorySlug" element={<ArticlesByCategoryPage />} />
              <Route path="/articles/tag/:tagSlug" element={<ArticlesByTagPage />} />
              <Route path="/articles/:slug" element={<ArticleDetail />} />

              {/* Auth Routes - redirect to dashboard if already authenticated */}
              <Route
                path="/login"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <Login />
                  </ProtectedRoute>
                }
              />

              <Route path="/verify" element={<VerifyAccount />} />
              <Route path="/verify-account" element={<VerifyAccount />} />
              <Route
                path="/forgot-password"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <ForgotPassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <ResetPassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute requireAuth={true}>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requireAuth={true}>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              {/* Admin Management Routes — unified structure + legacy redirects */}
              <Route
                path="/admin/structure"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <AcademicStructure />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/departments/*"
                element={<Navigate to="/admin/structure?tab=departments" replace />}
              />
              <Route
                path="/admin/academic-years/*"
                element={<Navigate to="/admin/structure?tab=years" replace />}
              />
              {/* Removed /admin/criteria routes */}

              {/* Event Management Routes */}
              <Route
                path="/manager/events"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <EventList />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/events/create"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <CreateEvent />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/events/:id"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <EventDetail />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/events/:id/edit"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <EditEvent />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/events/:id/article"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <ArticleEditor />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/articles/analytics"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <ArticleAnalyticsDashboard />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/articles/create"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <ArticleEditorById />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/articles/:articleId/edit"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <ArticleEditorById />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/articles/:articleId/comments"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <CommentManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/articles/categories"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <CategoriesManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/articles/tags"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <TagsManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/articles/analytics"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN]}>
                    <ManagerLayout>
                      <ArticleAnalyticsDashboard />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/articles"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <AdminArticleManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/articles"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN]}>
                    <ManagerLayout>
                      <AdminArticleManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/articles/create"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN]}>
                    <ManagerLayout>
                      <ArticleEditorById />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/articles/:articleId/edit"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN]}>
                    <ManagerLayout>
                      <ArticleEditorById />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/articles/:articleId/comments"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN]}>
                    <CommentManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/articles/categories"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN]}>
                    <ManagerLayout>
                      <CategoriesManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/articles/tags"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN]}>
                    <ManagerLayout>
                      <TagsManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/preparation"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <PreparationManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/preparation/:activityId"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER, Role.STUDENT]}>
                    <ManagerLayout>
                      <PreparationDetail />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/academic-years"
                element={<Navigate to="/admin/structure?tab=years" replace />}
              />
              <Route
                path="/admin/semesters/*"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN]}>
                    <ManagerLayout>
                      <SemesterManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/departments"
                element={<Navigate to="/admin/structure?tab=departments" replace />}
              />
              {/* Removed /admin/criteria route */}
              {/* Removed /admin/students route */}
              {/* Removed /admin/reports route */}
              <Route
                path="/admin/statistics"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <Statistics />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/accounts"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN]}>
                    <ManagerLayout>
                      <AccountManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/events/:id/registrations"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <EventRegistrations />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/classes"
                element={<Navigate to="/admin/structure?tab=classes" replace />}
              />
              <Route
                path="/manager/emails/send"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <SendEmail />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/emails/notifications/send"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <SendNotification />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/emails/history"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <EmailHistory />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/emails/history/:id"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <EmailDetail />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/notifications"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <NotificationList />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/notifications/:id"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <NotificationDetail />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />

              {/* Student Routes */}
              <Route
                path="/student/events"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentEvents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/events/:id"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentEventDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/series"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentSeries />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/series/:id"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentSeriesDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/minigames"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentMinigame />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/preparation"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentPreparation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/preparation/:activityId"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentPreparationDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/minigames/:activityId/play"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentMinigamePlay />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/qr-checkin"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <QRCodeCheckIn />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/minigames/:activityId/history"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentMinigameHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/registrations"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentRegistrations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/calendar"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentPersonalCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wishlist"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentWishlist />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/tasks"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentTasks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/participation-history"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentParticipationHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/reading-history"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentReadingHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/scores"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <ViewScores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/scores/appeals"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentScoreAppeals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/scores/appeals/:id"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentScoreAppealDetail />
                  </ProtectedRoute>
                }
              />
              {/* Removed /student/address route - address managed inside profile */}
              <Route
                path="/student/profile"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <StudentProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <NotificationList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications/:id"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.STUDENT]}>
                    <NotificationDetail />
                  </ProtectedRoute>
                }
              />

              {/* Manager Routes */}
              <Route
                path="/manager/registrations"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <ManagerRegistrations />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/scores"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <ManagerScores />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/scores/appeals"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <ManagerScoreAppeals />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/scores/appeals/:id"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <ManagerScoreAppeals />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/tasks"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <TaskManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/activity-tasks"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <ActivityTaskDashboard />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/series"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <SeriesManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/series/create"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <CreateSeries />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/series/:id"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <SeriesDetail />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/series/:id/edit"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <EditSeries />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/minigames"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <MinigameManagement />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/minigames/create"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <CreateMinigameWizard />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/minigames/create-quiz"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <CreateMinigame />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/minigames/edit/:miniGameId"
                element={
                  <ProtectedRoute requireAuth={true} allowedRoles={[Role.ADMIN, Role.MANAGER]}>
                    <ManagerLayout>
                      <EditQuiz />
                    </ManagerLayout>
                  </ProtectedRoute>
                }
              />

              {/* New utility pages */}
              <Route path="/manager/tasks/:taskId/submissions" element={<TaskSubmissionsRoute />} />
              {/* Removed criteria-based training score tool route */}
              <Route path="/tools/view-scores" element={<ViewScores />} />

              {/* Redirect any unknown routes to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <ToastContainer position="top-right" autoClose={2500} hideProgressBar />
              </div>
            </WishlistProvider>
          </HelmetProvider>
        </QueryClientProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
